import {
  Box,
  Card,
  Typography,
  TextField,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Button,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { multiInputFormat } from "../../../../../../utils/Constant";

type FormValues = {
  label: string;
  inputType: string;
  inputFormat?: string;
  answerOptions: string;
  isSum: boolean;
  width: number;
};

const ValidationSchema = yup.object({
  label: yup.string().required("This field can't be empty"),
  inputFormat: yup.string().when("inputType", {
    is: (value: string) => value === "OpenEnd",
    then: (schema) => schema.required("This field can't be empty"),
    otherwise: (schema) => schema.optional(),
  }),
  answerOptions: yup.string().when("inputType", {
    is: (value: string) => value === "SingleSelect",
    then: (schema) => schema.required("This field can't be empty"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export default function ColumnSettings({
  columnData,
  isEdit,
  onSave,
  onCancel,
  onDelete,
  totalWidth,
}: any) {
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      label: columnData?.label !== "New Column" ? columnData?.label : "",
      inputType: columnData?.inputType || "OpenEnd",
      inputFormat: columnData?.inputFormat || "",
      answerOptions: columnData?.answerOptions || "",
      isSum: columnData?.isSum ?? false,
      width: columnData?.width || 0,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      onSave({ ...values, column: columnData.column });
    },
  });

  const disableColumnWidth = (index: number) => {
    const width = columnData?.width === 0 ? 1 : formik.values.width;
    return 12 - totalWidth + width <= index;
  };

  return (
    <Box mt={5}>
      <Card className="card-layout">
        <Typography variant="h5">Column Settings</Typography>
        <form onSubmit={formik.handleSubmit}>
          <Box className="display-name-layout">
            <Typography className="label-txt">Column Label</Typography>
            <TextField
              id="label"
              name="label"
              fullWidth
              variant="outlined"
              placeholder="New Column"
              value={formik.values.label}
              onChange={formik.handleChange}
              error={formik.touched.label && Boolean(formik.errors.label)}
              helperText={formik.touched.label && formik.errors.label}
            />
          </Box>
          <Divider />
          <Box className="user-name-layout">
            <Typography className="label-txt">Input Type</Typography>
            <Box>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="inputType"
                  onChange={formik.handleChange}
                  value={formik.values.inputType}
                >
                  <FormControlLabel
                    value="OpenEnd"
                    control={<Radio />}
                    label="Text Input"
                  />
                  <FormControlLabel
                    value="SingleSelect"
                    control={<Radio />}
                    label="Select From Answer Options"
                  />
                  <FormControlLabel
                    value="TextField"
                    control={<Radio />}
                    label="Fixed Values"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
          <Divider />
          {formik.values.inputType === "OpenEnd" && (
            <>
              <Box className="user-name-layout">
                <Typography className="label-txt">Input Format</Typography>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.inputFormat &&
                    Boolean(formik.errors.inputFormat)
                  }
                >
                  <Select
                    labelId="inputFormat-select-label"
                    id="inputFormat-select"
                    name="inputFormat"
                    value={formik.values.inputFormat}
                    onChange={formik.handleChange}
                  >
                    {multiInputFormat.map((format) => (
                      <MenuItem value={format.value}>{format.label}</MenuItem>
                    ))}
                  </Select>
                  {Boolean(formik.errors.inputFormat) &&
                    formik.touched.inputFormat && (
                      <FormHelperText>
                        {formik.errors.inputFormat}
                      </FormHelperText>
                    )}
                </FormControl>
              </Box>
              <Divider />
            </>
          )}
          {formik.values.inputType === "SingleSelect" && (
            <>
              <Box className="user-name-layout">
                <Typography className="label-txt">Answer Options</Typography>
                <TextField
                  fullWidth
                  id="answerOptions"
                  name="answerOptions"
                  variant="outlined"
                  value={formik.values.answerOptions}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.answerOptions &&
                    Boolean(formik.errors.answerOptions)
                  }
                  helperText={
                    formik.touched.answerOptions &&
                    Boolean(formik.errors.answerOptions)
                      ? formik.errors.answerOptions
                      : "Separate by comma."
                  }
                />
              </Box>
              <Divider />
            </>
          )}
          {formik.values.inputType === "OpenEnd" &&
            (formik.values.inputFormat === "NumbersOnly" ||
              formik.values.inputFormat === "Currency" ||
              formik.values.inputFormat === "Percentage") && (
              <>
                <Box className="user-name-layout">
                  <Typography className="label-txt">
                    Calculate Sum of This <br /> Column?
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isSum"
                        checked={formik.values.isSum}
                        onChange={formik.handleChange}
                      />
                    }
                    label={formik.values.isSum ? "Yes" : "No"}
                  />
                </Box>
                <Divider />
              </>
            )}
          <Box className="user-name-layout">
            <Box
              sx={{
                width: 192,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                Column Width
              </Typography>
              <Tooltip
                title="The total area is broken into 12 units wide. Set to “Auto” if you would like this column to fill any unassigned space, or set a value if you would like this column to occupy a set amount of space.
The total of all assigned columns cannot exceed 12, and each column must occupy at least 1 space. Example 1: You have 4 columns and you would like column 1 to occupy half of the space, you would set column 1 to 6 units. With the remaining 3 columns set to “Auto”, they would all automatically occupy 2 spaces each. Example 2: You have 5 columns and you want column 5 to occupy as much space as possible. You would set column 5 to “Maximum”, and the remaining columns to “Auto”, and they would all occupy 1 unit each."
                arrow
                placement="top"
              >
                <IconButton sx={{ p: 0, mr: 2 }}>
                  <InfoOutlinedIcon color="primary" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ width: "calc(100% - 180px)" }}>
              <FormControl fullWidth>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  name="width"
                  value={formik.values.width}
                  onChange={formik.handleChange}
                >
                  <MenuItem value={0} disabled={totalWidth > 12}>
                    Auto
                  </MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                    (unit, index) => (
                      <MenuItem
                        value={unit}
                        disabled={disableColumnWidth(index)}
                      >
                        {unit} Unit
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: isEdit ? "space-between" : "flex-end",
              alignItems: "center",
              mt: 3,
            }}
          >
            {isEdit && (
              <Button
                startIcon={<DeleteOutlinedIcon color="error" />}
                variant="text"
                color="error"
                onClick={() => onDelete(columnData.column)}
              >
                Delete Column
              </Button>
            )}
            <Box>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ ml: 3 }}
                onClick={(e) => formik.handleSubmit()}
              >
                Save
              </Button>
            </Box>
          </Box>
        </form>
      </Card>
    </Box>
  );
}
